import type { AudioContent } from "@superego/backend";
import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import convertToWav from "./convertToWav.js";

interface UseRecordAudio {
  isRecording: boolean;
  startRecording: () => void;
  finishRecording: () => void;
  cancelRecording: () => void;
}
export default function useRecordAudio(
  onFinish: (audio: AudioContent) => void,
  audioBitsPerSecond = 16_000,
): UseRecordAudio {
  const [isRecording, setIsRecording] = useState(false);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const hasCanceledRef = useRef(false);

  // Cleanup on unmount.
  useEffect(() => {
    return () => cleanup(mediaStreamRef, mediaRecorderRef, hasCanceledRef);
  }, []);

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
    const mimeType = getPreferredMimeType();
    const mediaRecorder = new MediaRecorder(stream, {
      audioBitsPerSecond,
      ...(mimeType ? { mimeType } : null),
    });
    mediaRecorderRef.current = mediaRecorder;

    const recordedChunks: BlobPart[] = [];
    mediaRecorder.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) {
        recordedChunks.push(evt.data);
      }
    };

    mediaRecorder.onstop = async () => {
      try {
        if (!hasCanceledRef.current) {
          const chunksBlob = new Blob(recordedChunks, {
            type: mediaRecorder.mimeType,
          });
          onFinish({
            content:
              mediaRecorder.mimeType === "audio/wav"
                ? new Uint8Array(await chunksBlob.arrayBuffer())
                : await convertToWav(chunksBlob),
            contentType: "audio/wav",
          });
        }
      } finally {
        cleanup(mediaStreamRef, mediaRecorderRef, hasCanceledRef);
      }
    };

    mediaRecorder.start();
    setIsRecording(true);
  }, [onFinish, audioBitsPerSecond]);

  const finishRecording = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop();
    } catch {}
    setIsRecording(false);
  }, []);

  const cancelRecording = useCallback(() => {
    try {
      hasCanceledRef.current = true;
      mediaRecorderRef.current?.stop();
    } catch {}
    setIsRecording(false);
  }, []);

  return { isRecording, startRecording, finishRecording, cancelRecording };
}

const PREFERRED_MIME_TYPES = [
  "audio/wav",
  "audio/webm",
  "audio/ogg",
  "audio/mp4",
];

function getPreferredMimeType(): string | undefined {
  return PREFERRED_MIME_TYPES.find((type) =>
    MediaRecorder.isTypeSupported(type),
  );
}

function cleanup(
  mediaStreamRef: RefObject<MediaStream | null>,
  mediaRecorderRef: RefObject<MediaRecorder | null>,
  hasCanceledRef: RefObject<boolean>,
) {
  hasCanceledRef.current = false;
  try {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
  } catch {}
  mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
}
